import { logger } from '../services/logger.ts';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, AuthChangeEvent } from '@supabase/supabase-js';
import { supabase } from './db.ts';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  signIn: async () => {},
  signOut: async () => {}
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up auth state change listener first
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event: AuthChangeEvent, session) => {
      logger.info(`Auth state changed: ${event}, Session user: ${session?.user?.email || 'null'}`);
      
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
                  if (session?.user) {
            logger.info(`Setting user from session: ${session.user.email}`);
            setUser(session.user);
          } else {
            // Fallback to get user if session user is not available
            const { data: { user: currentUser } } = await supabase.auth.getUser();
            logger.info(`Setting user from getUser fallback: ${currentUser?.email || 'null'}`);
            setUser(currentUser);
          }
      } else if (event === 'SIGNED_OUT') {
        logger.info('SIGNED_OUT event received, clearing user state');
        setUser(null);
      }
    });

    // Get initial session
    const initializeAuth = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          logger.error('Error getting session:', sessionError.message);
          setUser(null);
        } else if (session?.user) {
          setUser(session.user);
        } else {
          setUser(null);
        }
      } catch (err) {
        logger.error('Error initializing auth:', err);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    // Log login attempt
    logger.info(`Login attempt for email: ${email}`);
    
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    
    if (error) {
      // Log failed login attempt with error details
      await logger.logFailedLogin(email, error.message, {
        errorCode: error.status,
        timestamp: new Date().toISOString(),
        errorType: error.name,
        errorDetails: error.message
      });
      throw error; // Throw the original error to preserve the error type
    }
    
    if (!data.user) {
      // Log failed login attempt when no user is returned
      await logger.logFailedLogin(email, 'No user data returned', {
        timestamp: new Date().toISOString(),
        errorType: 'NoUserData'
      });
      throw new Error('No user data returned');
    }
    
          // Log successful login
      await logger.logSuccessfulLogin(email, data.user.id, {
        timestamp: new Date().toISOString(),
      });
    
    setUser(data.user);
  };

  const signOut = async () => {
    try {
      logger.info('Sign out initiated');
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      logger.info('Sign out successful, clearing user state');
      setUser(null);
    } catch (error) {
      logger.error('Sign out error:', error);
      if (error instanceof Error) {
        throw new Error(error.message);
      }
      throw new Error('An error occurred during sign out');
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}